import React from 'react';
import { graphql } from 'gatsby';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import { usePostHog } from 'posthog-js/react';
import { useDniProvider } from '../../dni-provider';
import { useLocator } from '../locator-provider';
import { useTemplateProvider } from '../../template-provider';
import { PageTemplate } from '../../../utils/match-page-template';
import { FranchiseData } from '../../../sourcing/source-nodes/sources/franchises';
import type { FranchiseReview } from '../../../sourcing/testimonials/franchise-review';
import type { SiteNavigationSlices } from '../../../components/header/navigation/site-navigation-types';

export interface BaseMicrositeData {
    franchise: (FranchiseData & {
        newMenu: SiteNavigationSlices | null,
        footer: Queries.CsFranchiseFooterNav | null,
        validUrls: (string | null)[] | null,
        servicesProvidedReadable: Queries.FranchiseMicrositeDataFragment['servicesProvidedReadable'],
        servicesProvidedFilterable: Queries.FranchiseMicrositeDataFragment['servicesProvidedFilterable'],
    }) | null,
}

interface MicrositeContextReturnable extends BaseMicrositeData {
    isMicrosite: boolean,
    isLocalCityPage: boolean,
    isMicrositeHomepage: boolean;

    isHyperLocalTemplate: boolean
    isMetroTemplate: boolean
    isMetroStateTemplate: boolean

    name: string|null
    permalink: string | null,

    newMenu: SiteNavigationSlices | null,
    footer: Queries.CsFranchiseFooterNav | null,
    validUrls: (string | null)[] | null,
    targetCity: {
        city?: string | null
        state?: string | null
        content?: string | null
    } | null
    validServices: Queries.FranchiseMicrositeDataFragment['validServices'],
    reviews: FranchiseReview[] | null,
}

export const MicrositeContext = React.createContext(null as unknown as MicrositeContextReturnable);

interface MicrositeProps {
    children: React.ReactNode
    data?: {
        targetCity?: Queries.TargetCity
        franchise?: Queries.Franchise
        cmsMetroPage?: Queries.CsMetroPage
        franchiseReviews?: FranchiseReview[] | null
    }
}

const addToDataLayer = (eventName: string, key: string, value: string | number | boolean | null): void => {
    // value must not be null or undefined but maybe be a boolean and be false
    if (value === null || value === undefined) {
        return;
    }
    // dataLAyer should already be initialized as both gatsby-browser and GTM conditionally initializes it
    if ('dataLayer' in window && Array.isArray(window.dataLayer)) {
        // gets the most recent item of the same event type
        const ofEventType = window.dataLayer.filter(item => item.event === eventName);
        const lastOfEventType = ofEventType[ofEventType.length - 1];

        // Update only if either (a) we don't already have that event, or (b) we do, but its value for 'key' is different from 'value'
        if (!lastOfEventType || lastOfEventType[key] !== value) {
            window.dataLayer.push({
                event: eventName,
                [key]: value,
            });
        }
    }
};

export type ExtendedFranchiseData = FranchiseData & {
    wikipediaBasedGeographicAreas?: {
        places?: { city: string, state: string, latitude?: number, longitude?: number }[]
    },
    validUrls?: (string | null)[] | null,
    validServices: Queries.FranchiseMicrositeDataFragment['validServices'],
    newMenu: SiteNavigationSlices | null,
    footer?: Queries.CsFranchiseFooterNav | null
};

const MicrositeProvider = ({ children, data }: MicrositeProps): JSX.Element => {
    const { pathname } = useLocation();

    const { numberMap } = useDniProvider();
    const { franchise: locatorFranchise } = useLocator();

    const franchise = React.useMemo(() => {
        if (!data) return null;
        const sourceFranchise = data.franchise || data.cmsMetroPage?.franchises?.[0];
        if (!sourceFranchise) {
            return null;
        }
        const franchiseData = {
            ...sourceFranchise,
            servicesProvidedReadable: sourceFranchise.servicesProvidedReadable?.map(item => ({
                serviceLine: item?.serviceLine || '',
                sort: item?.sort || 0,
            })) || [],
            servicesProvidedFilterable: sourceFranchise.servicesProvidedFilterable?.map(item => ({
                serviceLine: item?.serviceLine || '',
                sort: item?.sort || 0,
            })) || [],
        };
        return franchiseData as unknown as ExtendedFranchiseData;
    }, [data]);

    const {
        name,
        validUrls,
        validServices,
        newMenu,
        footer,
        mainPhone,
    } = franchise || {};
    const { template: matchedTemplate } = useTemplateProvider();
    if (franchise && mainPhone && 'mainPhone' in franchise && numberMap && mainPhone in numberMap) {
        franchise.mainPhone = numberMap[mainPhone];
    }

    const isMicrosite = matchedTemplate === PageTemplate.MicrositePage;

    const isHyperLocalTemplate = matchedTemplate === PageTemplate.HyperLocal;
    const isMetroTemplate = matchedTemplate === PageTemplate.Metro;
    const isMetroStateTemplate = matchedTemplate === PageTemplate.MetroState;

    const isLocalCityPage = !!data?.targetCity;
    const permalink = franchise?.permalink || '';

    const targetCity = (() => {
        const source = data?.targetCity
        || data?.franchise?.serviceCities?.[0];

        return {
            city: source?.city || franchise?.address?.city,
            state: source?.state || franchise?.address?.state,
            content: source?.content || '',
        };
    })();

    const reviews = data?.franchiseReviews || null;
    const franchiseNumber = franchise?.franchiseNumber
        ?? locatorFranchise?.data?.franchiseNumber;

    const providerValue = React.useMemo(() => {
        const fD: MicrositeContextReturnable = {
            name: name || null,
            validUrls: validUrls || null,
            validServices: validServices || [],
            footer: footer || null,
            franchise: franchise as BaseMicrositeData['franchise'],
            isHyperLocalTemplate,
            isLocalCityPage,
            isMetroStateTemplate,
            isMetroTemplate,
            isMicrosite,
            isMicrositeHomepage: Boolean(isMicrosite && permalink && pathname === permalink),
            newMenu: newMenu || null,
            permalink: permalink || null,
            targetCity,
            reviews,
        };
        return fD;
    }, [
        name,
        franchise,
        permalink,
        newMenu,
        footer,
        validUrls,
        validServices,
        isMicrosite,
        isLocalCityPage,
        targetCity,
        isHyperLocalTemplate,
        isMetroTemplate,
        isMetroStateTemplate,
        pathname,
        reviews,
    ]);

    React.useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('Franchise ID', franchiseNumber);
        } else {
            // eslint-disable-next-line no-console
            window.fID = () => {
                // eslint-disable-next-line no-console
                console.log('Franchise ID', franchiseNumber);
                return franchiseNumber;
            };
        }
        // Ignore the ESLint, we only want to log this once for devs
    }, [franchiseNumber]);

    const posthog = usePostHog();

    React.useEffect(() => {
        if (isMicrosite && franchise?.franchiseNumber) {
            posthog?.capture('microsite_visit', { franchise_number: franchise.franchiseNumber });
        }
        addToDataLayer('SiteFranchiseNumber', 'FranchiseNumber', franchise?.franchiseNumber || 3000);
    }, [isMicrosite, franchise?.franchiseNumber, posthog]);

    return (
        <MicrositeContext.Provider value={providerValue}>
            {children}
        </MicrositeContext.Provider>
    );
};

export const useMicrosite = (): MicrositeContextReturnable => {
    const ctx = React.useContext(MicrositeContext);
    if (!ctx) {
        throw new Error('useMicrosite used outside of context');
    }
    return ctx;
};

export default MicrositeProvider;

export const query = graphql`
    fragment FranchiseMicrositeData on Franchise {
      metaSuffix
      wikipediaBasedGeographicAreas
      legacySiteEnabled
      tempOutOfService
      googleAdsTag
      uniqueContent
      activeMarkets {
        city
        state
        priority
        link
      }
      areaServedLocationData {
        city
        state
        latitude
        longitude
      }
      relevantAnnouncements {
        uid
        title
        copy
        enable
        callToAction {
          title
          href
        }
      }
      crewPhotos {
        url
        caption
        rawCaption
        name
        asset(targetWidth: 600)
      }
      averageRating
      reviewCount
      reviewSummary {
        shouldDisplay
        showRating
        rating
        count
      }
      permalink
      franchiseNumber
      profileExcerpt
      validUrls
      validServices {
        serviceLineName
        sort
        franchiseServiceMapping
        nationalServicePageLink
        micrositeLink
        icon {
          title
          url
        }
      }
      footer {
        links {
          title
          url
          className
          links {
            title
            url
            className
          }
        }
      }
      newMenu {
        id
        title
        primary {
          title
          link
          itemInfo {
            title
            content
            contentAsPlainText
            image {
              url
              title
              filename
              description
              contentStackAsset(width: 720)
            }
          }
          items {
            title
            link
            icon {
              url
              title
            }
            itemInfo {
              title
              content
              contentAsPlainText
              image {
                url
                title
                filename
                description
                contentStackAsset(width: 720)
              }
            }
            items {
              link
              title
              icon {
                url
                title
              }
            }
          }
        }
        secondary {
          title
          link
          items {
            title
            link
            items {
              title
              link
            }
          }
        }
      }
      socialMedia {
        facebook
        twitter
        instagram
        linkedIn
      }
      paymentMethods {
        americanExpress
        discover
        masterCard
        visa
      }
      profileExcerpt
      aboutUs {
        useCareerPlug
        careerPlugUrl
        ownerPicture
        ownerPictureAsset(targetWidth: 800, targetHeight: 700)
        displayOwners
        multipleOwners
        owners
        affiliations {
          description
        }
        awards {
          description
        }
        certifications {
          description
        }
      }
      licenses {
        generalContractorLicense
        residentialContractorLicense
        commercialContractorLicense
        plumbingContractorLicense
        electricalContractorLicense
        cslbContractorLicense
        moldRemediationContractorLicense
        biohazardContractorLicense
      }
      snapaStatus
      rapaStatus
      capaStatus
      averageRating
      reviewCount
      googleReviewLink
      servicesProvidedReadable {
        serviceLine
        sort
      }
      servicesProvidedFilterable {
        serviceLine
        sort
      }
      name
      address {
        latitude
        longitude
        city
        countryCode
        address
        addressExt
        postalCode
        state
      }
      googlePlaceId
      mainPhone
      addressHidden
    }
    
    fragment AllFranchiseReviewData on FranchiseReview {
      id
      name
      review
      reviewHtml
      city
      state
      date
      starRating
      photoUrl
      franchiseNumber
      showGoogleBadge
    }
`;
